import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faHeart, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { glossary } from '@sorare/core/src/lib/glossary';

import { OwnerMyClubLink } from 'components/TokenPreview/TokenOwner';

import type { WatchlistMetas_watchlist } from './__generated__/index.graphql';

const InlineHorizontal = styled(Horizontal)`
  display: inline-flex;
`;

const Metas = styled(LabelS)`
  svg {
    margin-right: var(--half-unit);
  }
`;

const Separator = () => (
  <LabelS color="var(--c-nd-150)" as="span">
    {' • '}
  </LabelS>
);

type Props = {
  watchlist: WatchlistMetas_watchlist;
  hideOwner?: boolean;
  withCreatedAt?: boolean;
  track?: () => void;
};

export const WatchlistMetas = ({
  watchlist,
  withCreatedAt = false,
  hideOwner = false,
  track,
}: Props) => {
  const { totalPlayersCount, createdAt, updatedAt } = watchlist;
  const { formatDistanceToNow, formatDate } = useIntlContext();

  const updatedAgo = formatDistanceToNow(updatedAt || createdAt);
  return (
    <Metas color="var(--c-nd-600)">
      {!!watchlist.user && !hideOwner && (
        <>
          <InlineHorizontal gap={0.5}>
            <OwnerMyClubLink
              user={watchlist.user}
              sport={watchlist.sport}
              withAvatar
              onClick={() => track?.()}
            />
          </InlineHorizontal>
          <Separator />
        </>
      )}
      {!!watchlist.subscriptionsCount && !!watchlist.public && (
        <>
          <FontAwesomeIcon icon={faHeart} color="var(--c-nd-600)" />
          <FormattedNumber
            notation="compact"
            maximumFractionDigits={1}
            value={watchlist.subscriptionsCount}
          />
          <Separator />
        </>
      )}
      <FontAwesomeIcon icon={faUserGroup} color="var(--c-nd-600)" />
      <FormattedMessage
        id="watchlistMetas.playersCount"
        defaultMessage="{totalPlayersCount, plural, one {# player} other {# players}}"
        values={{ totalPlayersCount }}
      />
      {!!withCreatedAt && (
        <>
          <Separator />
          <FormattedMessage
            {...glossary.created}
            values={{
              date: formatDate(createdAt),
            }}
          />
        </>
      )}
      <Separator />
      <FormattedMessage
        id="watchlistMetas.updated"
        defaultMessage="Updated {updatedAgo}"
        values={{ updatedAgo }}
      />
    </Metas>
  );
};

WatchlistMetas.fragments = {
  watchlist: gql`
    fragment WatchlistMetas_watchlist on Watchlist {
      slug
      id
      title
      totalPlayersCount
      createdAt
      updatedAt
      public
      sport
      user {
        slug
        ...OwnerMyClubLink_User
      }
      subscriptionsCount
    }
    ${OwnerMyClubLink.fragments.user}
  ` as TypedDocumentNode<WatchlistMetas_watchlist>,
};
